import { service } from '@ember/service';
import Model, { attr } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import { pushAndReturnPayload } from 'frontend-utils/utils/ember-data';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type AjaxService from 'lumen/services/ajax';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';

export interface SubscriptionProfileAddress {
  cityName: string;
  country: string;
  number: string;
  postalCode: string;
  street: string;
}

// UNIFIED SETTINGS ADDRESS PROPERTIES.
// IMPORTANT: keep the values in the array AddressProperties, synced with
// interface SubscriptionProfileAddress.
// This array does stores all the properties from SubscriptionProfileAddress
// which are needed to verify if the address was changes in Ember Data Store.
// Used in the method hasValuesChanges.
const AddressProperties: (keyof SubscriptionProfileAddress)[] = [
  'cityName',
  'country',
  'number',
  'postalCode',
  'street',
] as const;

export interface SubscriptionProfileAddressPayload {
  city_name: string;
  country: string;
  number: string;
  postal_code: string;
  street: string;
}

export interface SubscriptionProfilePayload {
  address: SubscriptionProfileAddressPayload;
  brand_url: string;
  email: string;
  hotel_id: string;
  hotel_name: string;
  language: string;
  latitude: number | null;
  logo_path: string;
  longitude: number | null;
  telnr: string;
  timezone: string;
}

export default class SubscriptionProfile extends Model {
  [Type] = 'subscription/profile' as const;

  @attr('subscription-profile-address')
  declare address: SubscriptionProfileAddress;

  @attr('string') declare brandUrl: string;

  @attr('string') declare email: string;

  @attr('string') declare hotelId: string;

  @attr('string') declare hotelName: string;

  @attr('string') declare language: string;

  @attr('number') declare latitude: number | null;

  @attr('string') declare logoPath: string;

  @attr('number') declare longitude: number | null;

  @attr('string') declare telnr: string;

  @attr('string') declare timezone: string;

  @service declare ajax: AjaxService;

  hasValuesChanges(ownedAttrs?: readonly string[]): boolean {
    if (!this.hasDirtyAttributes) {
      return false;
    }
    const changed = this.changedAttributes();
    const relevant = ownedAttrs
      ? Object.keys(changed).filter((k) => ownedAttrs.includes(k))
      : Object.keys(changed);

    if (relevant.length === 0) {
      return false;
    }

    if (relevant.includes('latitude') || relevant.includes('longitude')) {
      return true;
    }

    if (relevant.includes('address')) {
      const [before, after] = changed['address'] as unknown as [
        SubscriptionProfileAddress | undefined,
        SubscriptionProfileAddress | undefined,
      ];

      if (
        AddressProperties.some(
          (k) => (before?.[k] ?? '') !== (after?.[k] ?? ''),
        )
      ) {
        return true;
      }
    }

    return relevant.some((k) => k !== 'address');
  }

  async saveForSubscription(
    subscriptionId: string,
  ): Promise<SubscriptionProfile> {
    const data = this.serialize() as Record<string, unknown>;

    const url = formatUrl(
      `/${APIGATEWAY}/configure/subscription/profile`,
      { subscription_id: subscriptionId },
      this.ajax.host,
    );
    const response: SubscriptionProfilePayload = await this.ajax.request(url, {
      data: JSON.stringify(data),
      method: this.isNew ? 'POST' : 'PUT',
      dataType: 'json',
      contentType: 'application/json; charset=utf-8',
    });

    super.rollbackAttributes();

    const normalisedResponse = pushAndReturnPayload(
      this.store,
      { 'subscription-profiles': [{ ...response, id: subscriptionId }] },
      'subscription/profile',
    );

    return (
      normalisedResponse['subscription-profiles'] as SubscriptionProfile[]
    )[0]!;
  }
}
